import React, { FC } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import Image from 'gatsby-image';
import { useIntl } from 'react-intl';
import styled from '@emotion/styled';
import { Row, Col } from 'emotion-flex';
import hexToRgba from 'hex-to-rgba';
import Container from './Container';
import Link from '../Link';
import presets from '../../styles/presets';
import { ReactComponent as Logo } from '../../assets/images/logo.svg';
import { ReactComponent as LogoSodbrennen } from '../../assets/images/logo-sodbrennen.svg';
import { ContentfulFooterConnection } from '../../graphql-types';
import { MONO_SYSTEM_FONTS, SYSTEM_FONTS } from '../../styles/typography';
import { File } from '../../graphql-types';
import Markdown from 'react-markdown/with-html';
import { ReactComponent as TopWave } from '../../assets/svgs/product/blue-footer-top-old.svg';
import { ReactComponent as FooterLine } from '../../assets/svgs/product/img-footer-line-old.svg';
import { ReactComponent as AbstractShape } from '../../assets/svgs/product/img-footer_decoration.svg';
import { ReactComponent as CTAImage } from '../../assets/svgs/product/img-mainCTAtease.svg';
import { ReactComponent as HeartburnCTA } from '../../assets/svgs/heartburn/img-mainCTAtease-Study.svg';
import { ReactComponent as Sparks } from '../../assets/svgs/product/img-sparks-cta.svg';
import { typeformEligiblityCheckerLink } from '../../utils/constants';
import { ButtonLink } from '../common/Button';
import { RecoletaHeading } from '../common/typography';
import TypeformEmbedButton from './2023wk35/TypeformOverlay';

const FooterHeading = styled('span')({
  marginBottom: '1.5rem',
  fontFamily: ['Source Code Pro'].concat(MONO_SYSTEM_FONTS).join(', '),
  fontWeight: 600,
  fontSize: '0.75rem',
  letterSpacing: 1.5,
  color: hexToRgba('#160B2C', 0.9),
});

const FooterLink = styled(Link)({
  fontFamily: ['Source Code Pro'].concat(MONO_SYSTEM_FONTS).join(', '),
  fontWeight: 500,
  fontSize: '0.75rem',
  letterSpacing: 1.5,
  color: 'inherit',
  textDecoration: 'none',
  marginBottom: '1rem',
  transition: 'opacity 0.15s cubic-bezier(0.4, 0.1, 1, 1)',

  '@media (hover: hover)': {
    '&:hover': {
      color: '#160B2C',
      textDecoration: 'none',
    },
  },
});

const FooterLegalLink = styled(Link)({
  display: 'block',
  padding: '0 1rem',
  fontWeight: 500,
  fontFamily: ['Source Code Pro'].concat(MONO_SYSTEM_FONTS).join(', '),
  fontSize: '0.75rem',
  color: hexToRgba('#160B2C', 0.6),
  letterSpacing: 0.5,
  transition: 'opacity 0.15s cubic-bezier(0.4, 0.1, 1, 1)',

  '@media (hover: hover)': {
    '&:hover': {
      color: '#160B2C',
      textDecoration: 'none',
    },
  },
  '&:first-of-type': {
    paddingLeft: 0,
  },
  '&:last-child': {
    paddingRight: 0,
  },
});

interface Props {
  type: 'prescription' | 'heartburn';
  footerData?: {
    'de': {
      title: string;
      text: string;
      buttonText: string;
      buttonUrl: string;
      buttonType: '' | 'typeform' | 'link';
    },
    'en': {
      title: string;
      text: string;
      buttonText: string;
      buttonUrl: string;
      buttonType: '' | 'typeform' | 'link';
    }
  };
}

const Footer: FC<Props> = ({ type, footerData }) => {
  const intl = useIntl();
  const { footers, image } = useStaticQuery<{
    footers: ContentfulFooterConnection;
    image: File;
  }>(graphql`
    {
      footers: allContentfulFooter {
        nodes {
          node_locale
          linkGroups {
            id
            text
            links {
              id
              text
              url
              isDisplayed
            }
          }
          copyrights
          legalLinks {
            id
            text
            url
            isDisplayed
          }
        }
      }
      image: file(relativePath: { regex: "/ce-mark/i" }) {
        childImageSharp {
          fixed(width: 80) {
            ...GatsbyImageSharpFixed
          }
        }
      }
    }
  `);
  const footer = footers.nodes.find((node) => node.node_locale === intl.locale);

  const footerDataLocaled = intl.locale === 'de' ? footerData.de : footerData.en;

  const prescriptionCTA = () => (
    <div
      css={{
        marginTop: -1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: 'calc(15vw)',
        textAlign: 'center',
        background: '#3D72DF',
      }}
    >
      <CTAImage
        css={{
          marginBottom: '1rem',
        }}
      />
      <RecoletaHeading
        css={{
          fontSize: 32,
          fontWeight: 500,
          marginBottom: '1rem',
          color: '#ffffff',
        }}
      >
        {intl.formatMessage({
          id: 'homepage.v2.last.cta.title',
          defaultMessage: 'Worauf wartest du noch?',
        })}
      </RecoletaHeading>
      <p
        css={{
          fontFamily: ['Open Sans'].concat(SYSTEM_FONTS).join(', '),
          fontWeight: 500,
          fontSize: 16,
          maxWidth: 400,
          color: '#FFFFFF80',
        }}
      >
        {intl.formatMessage({
          id: 'homepage.v2.last.cta.text',
          defaultMessage:
            'Wir begleiten dich Schritt für Schritt auf deinem Weg zu spürbar weniger Reizdarm-Beschwerden',
        })}
      </p>

      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          marginBottom: '3rem',
        }}
      >
        <ButtonLink to={typeformEligiblityCheckerLink}>
          {intl.formatMessage({
            id: 'homepage.problems.cta.text',
            defaultMessage: 'Kostenfreie Therapie Starten',
          })}
        </ButtonLink>
        <div
          css={{
            position: 'absolute',
            top: 45,
            left: -45,
            zIndex: 1,
            display: 'none',

            [presets.md]: {
              display: 'block',
            },
          }}
        >
          <Sparks />
        </div>
      </div>
    </div>
  );

  const heartburnCTA = () => (
    <div
      css={{
        marginTop: -1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        paddingBottom: 'calc(15vw)',
        textAlign: 'center',
        background: '#3D72DF',
      }}
    >
      <HeartburnCTA
        css={{
          marginBottom: '1rem',
        }}
      />
      <RecoletaHeading
        css={{
          fontSize: 32,
          fontWeight: 500,
          marginBottom: '1rem',
          color: '#ffffff',
        }}
      >
        {footerDataLocaled.title}
      </RecoletaHeading>
      <p
        css={{
          fontFamily: ['Open Sans'].concat(SYSTEM_FONTS).join(', '),
          fontWeight: 500,
          fontSize: 16,
          maxWidth: 400,
          color: '#FFFFFF90',
        }}
      >
        <Markdown source={footerDataLocaled.text} escapeHtml={false} />
      </p>

      <div
        style={{
          position: 'relative',
          display: 'inline-block',
          marginBottom: '3rem',
        }}
      >
        <TypeformEmbedButton
          index={7}
          typeformId={footerDataLocaled.buttonUrl}
          buttonText={footerDataLocaled.buttonText}
          buttonStyle={{
            margin: '5px 10px',
            backgroundColor: '#F5D176',
            borderColor: '#F5D176',
            color: '#160B2C',
            fontWeight: 700,
            padding: '1rem 1rem',
            borderRadius: 8,
          }}
        />
        <div
          css={{
            position: 'absolute',
            top: 45,
            left: -45,
            zIndex: 1,
            display: 'none',

            [presets.md]: {
              display: 'block',
            },
          }}
        >
          <Sparks />
        </div>
      </div>
    </div>
  );

  return (
    <footer
      css={{
        position: 'relative',
        color: hexToRgba('#160B2C', 0.9),
      }}
    >
      <TopWave
        width="100%"
        height="43"
        css={{
          display: 'block',
          width: '100%',
          height: 'auto',
          marginTop: -1,
          position: 'relative',
          zIndex: 1,
        }}
      />

      {type === 'prescription' && prescriptionCTA()}
      {type === 'heartburn' && heartburnCTA()}
      <FooterLine
        width="100%"
        height="43"
        css={{
          display: 'block',
          width: '100%',
          height: 'auto',
          marginTop: '-12%',
          position: 'relative',
          zIndex: 1,
        }}
      />
      <AbstractShape
        css={{
          display: 'none',
          visibility: 'hidden',
          [presets.lg]: {
            display: 'none',
            visibility: 'hidden',
            position: 'absolute',
            right: '9.8%',
            top: '55%',
            zIndex: 2,
          },
        }}
      />
      <Container>
        <Row>
          <Col
            xs={12}
            lg={2}
            css={{
              marginBottom: 64,
            }}
          >
            <Link aria-label="Cara Care homepage" to="/">
              {type === 'heartburn' && <LogoSodbrennen width={130} height={30} />}
              {type === 'prescription' && <Logo width={130} height={30} />}
            </Link>
          </Col>
          {/* First row of group links */}
          {footer.linkGroups.map((group) => (
            <Col
              key={group.id}
              xs={6}
              lg={2}
              css={{
                marginBottom: 64,
              }}
            >
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <FooterHeading>{group.text}</FooterHeading>
                {group.links
                  .filter((link) => link.isDisplayed)
                  .map((link) => (
                    <FooterLink
                      key={link.id}
                      to={link.url}
                      css={{
                        fontFamily: ['Source Code Pro']
                          .concat(MONO_SYSTEM_FONTS)
                          .join(', '),
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        letterSpacing: 1.5,
                        color: 'inherit',
                        textDecoration: 'none',
                        marginBottom: '1rem',
                      }}
                    >
                      {link.text}
                    </FooterLink>
                  ))}
              </div>
            </Col>
          ))}
          {/* second row on mobile, on desktop LEGAL links are in a different place, that's why we don't display them here */}
          <Col
            xs={6}
            lg={2}
            css={{
              marginBottom: 64,
              [presets.lg]: {
                display: 'none',
                visibility: 'hidden',
              },
            }}
          >
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <FooterHeading>
                {intl.formatMessage({
                  id: 'footer.legal',
                  defaultMessage: 'Legal',
                })}
              </FooterHeading>
              {footer.legalLinks
                .filter((link) => link.isDisplayed)
                .map((link) => (
                  <FooterLink
                    key={link.id}
                    to={link.url}
                    css={{
                      fontFamily: ['Source Code Pro']
                        .concat(MONO_SYSTEM_FONTS)
                        .join(', '),
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      letterSpacing: 1.5,
                      color: 'inherit',
                      textDecoration: 'none',
                      marginBottom: '1rem',
                    }}
                  >
                    {link.text}
                  </FooterLink>
                ))}
            </div>
          </Col>
          <Col
            xs={12}
            lg={4}
            lgOffset={2}
            css={{
              marginBottom: '1.5rem',
              [presets.lg]: {
                textAlign: 'right',
              },
            }}
          >
            <Image
              css={{
                marginBottom: '.5rem',
              }}
              fixed={image.childImageSharp.fixed}
            />
            <span
              css={{
                display: 'block',
                fontWeight: 500,
                fontFamily: ['Source Code Pro']
                  .concat(MONO_SYSTEM_FONTS)
                  .join(', '),
                fontSize: '0.75rem',
                color: hexToRgba('#160B2C', 0.6),
                letterSpacing: 0.5,
              }}
            >
              {intl.formatMessage({
                id: 'footer.ceCertifiedMedicalDevice',
                defaultMessage: 'CE-certified Medical Device',
              })}
            </span>
            <span
              css={{
                display: 'block',
                fontWeight: 500,
                fontFamily: ['Source Code Pro']
                  .concat(MONO_SYSTEM_FONTS)
                  .join(', '),
                fontSize: '0.75rem',
                color: hexToRgba('#160B2C', 0.6),
                letterSpacing: 0.5,
              }}
            >
              {intl.formatMessage({
                id: 'footer.gdprCompliant',
                defaultMessage: 'GDPR Compliant',
              })}
            </span>
          </Col>
        </Row>
        <div
          css={{
            marginBottom: '1.5rem',
            [presets.lg]: {
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              visibility: 'visible',
            },
          }}
        >
          <span
            css={{
              fontWeight: 500,
              fontFamily: ['Source Code Pro']
                .concat(MONO_SYSTEM_FONTS)
                .join(', '),
              fontSize: '0.75rem',
              color: hexToRgba('#160B2C', 0.6),
              letterSpacing: 0.5,
            }}
          >
            {footer.copyrights}
          </span>
          <div
            css={{
              display: 'none',
              [presets.lg]: {
                display: 'flex',
                alignItems: 'center',
              },
            }}
          >
            {footer.legalLinks.map((link) => (
              <FooterLegalLink key={link.id} to={link.url}>
                {link.text}
              </FooterLegalLink>
            ))}
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
